// Voice-note transcription. Reuses the same waGetMessageMediaTool thunk
// the MCP `wa_get_message_media_by_key` tool drives — the thunk now owns
// the page-primitive call + extraction (Whisper for audio, pdfjs for
// PDF/text) entirely. Used by the "Transcribe" message-action button
// injected for audio messages.

import { createAsyncThunk } from '@reduxjs/toolkit';
import { waGetMessageMediaTool } from './WhatsappThunks';
import { AUTO_LANG } from '@common/reducers/TranscriptionPrefsReducer';
import { setAmbientTranscribeSourceLanguage } from '@extension/services/pdf-extraction';
import {
  chatIdFromMessageKey,
  getStickyChatLanguage,
  recordChatLanguage,
} from '@extension/services/transcribe-chat-language';
import {
  isAiLinkedChat,
  getAiLinkedChatLanguage,
  recordAiLinkedChatLanguage,
} from '@extension/context/content/services/ai-linked-chats-registry';
import type { TranscriptionResult } from '@bt-common/interfaces/speech-contracts.interface';

/** Mirror the user's TranscriptionPrefs.sourceLanguage from Redux
 *  state into chrome.storage.local and AWAIT the write before
 *  returning. The reducer's `persist` and the picker's onChange path
 *  both write to the same key, but through the promise-style
 *  `chrome.storage.local.set(...)` API — that's async and can lose a
 *  race against a very-quickly-following transcribe click. Doing it
 *  again here, callback-style + awaited, forces the storage into
 *  agreement with Redux BEFORE the offscreen document reads it.
 *
 *  Silent on failure — if chrome.storage is unavailable, or the write
 *  rejects, we still proceed to transcription. Worst case the
 *  offscreen falls back to auto-detect / generic model, which is what
 *  it did before this fix. */
async function syncLanguagePrefToChromeStorage(getState: () => unknown): Promise<void> {
  try {
    const state = getState() as { TranscriptionPrefsReducer?: { sourceLanguage?: string } };
    const lang = state.TranscriptionPrefsReducer?.sourceLanguage ?? AUTO_LANG;
    const cs: any = (globalThis as any).chrome;
    if (!cs?.storage?.local?.set) return;
    await new Promise<void>((resolve) => {
      try {
        cs.storage.local.set({ 'bt-transcribe-source-lang': lang }, () => {
          const err = cs.runtime?.lastError;
          if (err) {
            console.warn('[transcribe-thunk] eager sync failed', err);
          } else {
            console.log(`[transcribe-thunk] chrome.storage.local synced: ${lang}`);
          }
          resolve();
        });
      } catch (e) {
        console.warn('[transcribe-thunk] eager sync threw', e);
        resolve();
      }
    });
  } catch (e) {
    console.warn('[transcribe-thunk] state read failed', e);
  }
}

export const transcribeMessageThunk = createAsyncThunk<
  {
    text: string | null;
    language?: string | null;
    mimetype?: string;
    /** Structured bt-common speech-contract result — flags, model,
     *  timings, languageSource. Null when the tool call failed before
     *  reaching the transcription pipeline. */
    transcription?: TranscriptionResult | null;
  },
  { messageKey: string }
>(
  'transcribe/extractFromMessage',
  async ({ messageKey }, { getState }) => {
    // ── Language resolution — precedence order ─────────────────────
    //
    //   1. Explicit user pick in the Display Settings picker (non-auto)
    //      — a hard override, always wins.            origin: 'user'
    //   2. Sticky per-chat language — the language previous voice notes
    //      in THIS chat resolved to (≥2 consistent observations). Skips
    //      the detect pass; stabilises short clips.   origin: 'sticky'
    //   3. Auto-detect in the offscreen pipeline (6s clip).
    //
    // Redux is the AUTHORITATIVE source for (1) — no round-trip through
    // storage, no cross-context lag. The resolved value travels two ways:
    //   a. As an "ambient" value picked up by `extractPdfTextInContentScript`
    //      and forwarded in the EXTRACT_DOC message to offscreen.
    //   b. As a chrome.storage.local write for the (kept-as-safety-net)
    //      fallback read path in offscreen's readTranscribeSourceLanguage.
    let userPick: string = AUTO_LANG;
    try {
      const state = getState() as { TranscriptionPrefsReducer?: { sourceLanguage?: string } };
      userPick = state.TranscriptionPrefsReducer?.sourceLanguage ?? AUTO_LANG;
    } catch (e) {
      console.warn('[transcribe-thunk] state read failed', e);
    }

    const chatId = chatIdFromMessageKey(messageKey);
    let langForOffscreen: string = AUTO_LANG;
    let langOrigin: 'user' | 'sticky' | undefined;
    if (userPick !== AUTO_LANG) {
      langForOffscreen = userPick;
      langOrigin = 'user';
    } else {
      // btgpt AI-conversation language: the locked per-conversation language
      // (from the backend, or optimistically from this chat's first voice note)
      // takes precedence over the local sticky heuristic — it is the source of
      // truth and skips the detect pass from the SECOND voice note on.
      const linkedLang = getAiLinkedChatLanguage(chatId ?? undefined);
      if (linkedLang) {
        langForOffscreen = linkedLang;
        langOrigin = 'sticky';
      } else {
        const sticky = getStickyChatLanguage(chatId);
        if (sticky) {
          langForOffscreen = sticky;
          langOrigin = 'sticky';
        }
      }
    }
    setAmbientTranscribeSourceLanguage(langForOffscreen, langOrigin);
    console.log(`[transcribe-thunk] language resolved: ${langForOffscreen} (origin=${langOrigin ?? 'auto'}, chat=${chatId ?? 'unknown'})`);

    // Belt-and-suspenders storage write (kept for compatibility with
    // any older offscreen build still reading chrome.storage.local).
    // Deliberately writes the USER pick, not the sticky value — the
    // fallback path has no chat context and must not leak one chat's
    // language into another's.
    await syncLanguagePrefToChromeStorage(getState);

    // transcribeAudio: true tells the page-context to ship audio bytes for
    // offscreen Whisper transcription (extraction itself runs inside the
    // thunk now, see waGetMessageMediaTool preview branch). MCP callers
    // don't pass this flag so they don't pay the 10-30s Whisper cost.
    const resp = await waGetMessageMediaTool({ waMessageKey: messageKey, mode: 'preview', transcribeAudio: true });
    if (resp.success === false) return { text: null, transcription: null };
    const data = resp.data;
    if (data.kind !== 'preview') return { text: null, transcription: null };

    const transcription = (data as { transcription?: TranscriptionResult | null }).transcription ?? null;

    // ── Sticky-language learning ───────────────────────────────────
    // Only trustworthy outcomes teach the map: real text, a resolved
    // language, and no quality flags (a truncated transcript is still
    // trustworthy for language purposes). Explicit user picks also
    // teach — if the user locked Hebrew and it produced text in this
    // chat, remembering that is exactly right.
    if (
      transcription &&
      transcription.text &&
      transcription.language &&
      transcription.flags.every((f) => f === 'truncated')
    ) {
      recordChatLanguage(chatId, transcription.language);
      // For a btgpt AI-linked chat, remember it IMMEDIATELY (1 hit) so the next
      // voice note skips detection — the conversation locks its language on the
      // first message ("auto-detect first, then lock").
      if (isAiLinkedChat(chatId ?? undefined)) {
        recordAiLinkedChatLanguage(chatId ?? undefined, transcription.language);
      }
    }

    return {
      text: data.extractedText ?? null,
      language: data.language ?? null,
      mimetype: (data.metadata as { mimetype?: string } | undefined)?.mimetype,
      transcription,
    };
  },
);
